import * as stateActions from '../actions/stateActions';
import * as requestActions from '../actions/requestActions';
import * as sessionStates from '../sessionStates';
import { logSipEvent } from '../logger';
import { playAudio, getGlobalAudioElement, setAudioOutputDevice } from './audioUtils';
// import { releaseStream } from './streamUtils';

/**
 * Extract user identity from session
 */
export const extractUserIdentity = (session) => {
	let userIdentity = 'Unknown';
	let userUri = 'Unknown';
	try {
		const remoteIdentity = session.remote_identity || session.remoteIdentity;
		if (remoteIdentity) {
			userIdentity = remoteIdentity.display_name || 
				(remoteIdentity.uri && remoteIdentity.uri.user) || 
				(remoteIdentity.uri && remoteIdentity.uri.toString()) || 
				'Unknown';
			userUri = remoteIdentity.uri ? 
				(typeof remoteIdentity.uri.toString === 'function' ? 
					remoteIdentity.uri.toString() : 
					remoteIdentity.uri) : 
				'Unknown';
		}
	} catch (err) {
		console.warn('Could not extract user identity:', err);
	}
	return { userIdentity, userUri };
};

/**
 * Clean up media streams from session data
 */
const cleanupMediaStreams = (sessionData, logger) => {
	if (!sessionData) return;

	// Stop local tracks
	if (sessionData.localStream) {
		logger.debug('Stopping local tracks');
		sessionData.localStream.getTracks().forEach(track => {
			try {
				track.stop();
				logger.debug(`Stopped local track: ${track.kind}`);
			} catch (err) {
				logger.warn(`Error stopping local track: ${err.message}`);
			}
		});

		if (typeof sessionData.localStream.release === 'function') {
			try {
				sessionData.localStream.release();
			} catch (err) {
				logger.warn(`Error releasing local stream: ${err.message}`);
			}
		}
	}

	// Clean up remote stream
	if (sessionData.remoteStream) {
		logger.debug('Cleaning up remote stream');
		sessionData.remoteStream.getTracks().forEach(track => {
			try {
				track.stop();
				logger.debug(`Stopped remote track: ${track.kind}`);
			} catch (err) {
				logger.warn(`Error stopping remote track: ${err.message}`);
			}
		});

		if (typeof sessionData.remoteStream.release === 'function') {
			try {
				sessionData.remoteStream.release();
			} catch (err) {
				logger.warn(`Error releasing remote stream: ${err.message}`);
			}
		}
	}
};

/**
 * Setup session event handlers
 */
export const setupSessionHandlers = (session, callId, direction, store, logger, stopAllCameraResources, applySavedMuteState, clearVideoStreamsByTrackId) => {
	// Session ended handler
	session.on('ended', (e) => {
		logger.debug('Session ended');
		logSipEvent('session ended', e);

		stopAllCameraResources(session);

		store.dispatch(stateActions.setSessionState({
			sipSession: session,
			sessionState: sessionStates.TERMINATED
		}));

		try {
			const sessionData = store.getState().sessions[session.callId];
			cleanupMediaStreams(sessionData, logger);
		} catch (cleanupError) {
			logger.error('Media cleanup error:', cleanupError);
		}

		if (clearVideoStreamsByTrackId) {
			clearVideoStreamsByTrackId();
		}

		setTimeout(() => {
			try {
				logger.debug('Session removed');
				store.dispatch(stateActions.removeSession({ sipSession: session }));

				if (!store.getState().userStatus.currentSession) {
					const sessions = store.getState().sessions;
					if (sessions) {
						store.dispatch(
							stateActions.setCurrentSession({
								currentSession: Object.keys(sessions)[0]
							})
						);
					}
				}
			} catch (error) {
				logger.error('Session cleanup failed:', error);
				store.dispatch(requestActions.notify({
					type: 'error',
					text: `Session cleanup failed: ${error.message}`
				}));
			}
		}, 3000);
	});

	// Session failed handler
	session.on('failed', (event) => {
		logSipEvent('session failed', event);

		stopAllCameraResources(session);

		store.dispatch(stateActions.setSessionState({
			sipSession: session,
			sessionState: sessionStates.TERMINATED
		}));

		let errorMessage = 'Call failed to connect';
		let errorDetails = '';
		let statusCode = '';

		if (event && event.response && event.response.status_code) {
			statusCode = event.response.status_code;
			errorDetails = `SIP Status: ${statusCode}`;
		}

		if (event && event.cause) {
			switch (event.cause) {
				case 'Rejected':
					errorMessage = 'Call was rejected by the remote party';
					break;
				case 'Busy':
					errorMessage = 'Remote party is busy';
					break;
				case 'Not Found':
					errorMessage = 'Destination not found';
					break;
				case 'Not Acceptable':
					errorMessage = 'Call not acceptable: media negotiation failed';
					errorDetails = 'The remote device may not support the required codecs';
					break;
				case 'WebRTC Error':
					errorMessage = 'WebRTC connection error';
					errorDetails = 'Could not establish media connection';
					break;
				case 'User Denied Media Access':
					errorMessage = 'Microphone access denied';
					errorDetails = 'Please check browser permissions';
					break;
				case 'Connection Error':
					errorMessage = 'Connection failed: network problem';
					break;
				case 'Request Timeout':
					errorMessage = 'Request timed out: no response from server';
					break;
				case 'Transport Error':
					errorMessage = 'Network transport error';
					errorDetails = 'Connection to the SIP server failed';
					break;
				default:
					errorMessage = `Call failed: ${event.cause}`;
			}
		}

		// Handle specific SIP status codes
		if (statusCode) {
			if (statusCode === '403') {
				errorMessage = 'Authentication failed or call forbidden';
			} else if (statusCode === '404') {
				errorMessage = 'Destination not found (404)';
			} else if (statusCode === '480') {
				errorMessage = 'Temporarily unavailable';
			} else if (statusCode === '486') {
				errorMessage = 'Destination is busy';
			} else if (statusCode === '487') {
				errorMessage = 'Call canceled';
			} else if (statusCode === '503') {
				errorMessage = 'Service unavailable';
				errorDetails = 'The SIP server may be overloaded or down';
			}
		}

		// Check WebRTC connection state
		if (session.connection) {
			const iceState = session.connection.iceConnectionState;
			const connState = session.connection.connectionState;

			if (iceState === 'failed' || connState === 'failed') {
				errorDetails = `WebRTC ${iceState === 'failed' ? 'ICE' : 'connection'} failure. Check firewall settings.`;
			}

			logger.debug(`Connection state - ICE: ${iceState}, Connection: ${connState}`);
		}

		const finalMessage = errorDetails ?
			`${errorMessage}. ${errorDetails}` : errorMessage;

		store.dispatch(requestActions.notify({
			type: 'error',
			text: finalMessage,
			timeout: 8000
		}));

		try {
			const sessionData = store.getState().sessions[session.callId];
			cleanupMediaStreams(sessionData, logger);
		} catch (cleanupError) {
			logger.error('Media cleanup error for failed session:', cleanupError);
		}

		setTimeout(() => {
			try {
				logger.debug('Failed session removed');
				store.dispatch(stateActions.removeSession({ sipSession: session }));
			} catch (error) {
				logger.error('Session cleanup failed:', error);
			}
		}, 3000);
	});

	// Session progress handler
	session.on('progress', (e) => {
		logSipEvent('session progress', e);

		if (direction !== sessionStates.INCOMING) {
			store.dispatch(stateActions.setSessionState({
				sipSession: session,
				sessionState: sessionStates.ESTABLISHING
			}));
			logger.debug('Session state set to ESTABLISHING for outgoing call');
		} else {
			logger.debug('Keeping session state as NEW for incoming call during progress');
		}
	});

	// Session accepted handler
	session.on('accepted', (e) => {
		logSipEvent('session accepted', e);
		
		const { userIdentity, userUri } = extractUserIdentity(session);
		
		console.group('✅ CONFERENCE CALL - USER ACCEPTED CALL');
		console.log('📞 Session ID (Call-ID):', callId);
		console.log('👤 User Name:', userIdentity);
		console.log('📧 User URI:', userUri);
		console.log('📅 Timestamp:', new Date().toISOString());
		console.groupEnd();
		
		store.dispatch(stateActions.setSessionState({
			sipSession: session,
			sessionState: sessionStates.ESTABLISHING
		}));
	});

	// Session confirmed handler
	session.on('confirmed', (e) => {
		logSipEvent('session confirmed', e);
		
		const { userIdentity, userUri } = extractUserIdentity(session);
		
		console.group('🎉 CONFERENCE CALL - CALL CONFIRMED');
		console.log('📞 Session ID (Call-ID):', callId);
		console.log('👤 User Name:', userIdentity);
		console.log('📧 User URI:', userUri);
		console.log('📅 Timestamp:', new Date().toISOString());
		console.groupEnd();
		logger.debug('Session state:',
			`Connection: ${session.connection.connectionState},`,
			`ICE: ${session.connection.iceConnectionState}`);

		store.dispatch(stateActions.setSessionState({
			sipSession: session,
			sessionState: sessionStates.ESTABLISHED
		}));

		applySavedMuteState(session);

		try {
			const localStream = session.connection.getLocalStreams()[0];
			if (localStream && localStream.getAudioTracks().length > 0) {
				const dtmfSender = session.connection.createDTMFSender(localStream.getAudioTracks()[0]);
				session.sendDTMF = function (tone) {
					dtmfSender.insertDTMF(tone);
				};
			}

			setAudioOutputDevice('default');

			const audioElement = getGlobalAudioElement();
			if (audioElement && session.connection.getRemoteStreams()[0]) {
				setTimeout(() => {
					logger.debug('Attempting to play audio');
					playAudio(audioElement, session.connection.getRemoteStreams()[0])
						.then(success => {
							if (success) {
								logger.debug('Audio confirmed and playing');
								store.dispatch(requestActions.notify({
									type: 'success',
									text: 'Call connected successfully',
									timeout: 3000
								}));
							} else {
								logger.warn('Audio may not be playing correctly');
								store.dispatch(requestActions.notify({
									type: 'warning',
									text: 'Audio playback issue detected. Try clicking to enable audio.'
								}));
							}
						})
						.catch(error => {
							logger.error('Failed to play audio:', error);
							store.dispatch(requestActions.notify({
								type: 'error',
								text: 'Failed to play audio. Please try refreshing the page.'
							}));
						});
				}, 300);
			}
		} catch (error) {
			logger.error('DTMF setup failed:', error);
		}
	});

	// Session connecting handler
	session.on('connecting', (e) => {
		logSipEvent('session connecting', e);
		store.dispatch(stateActions.setSessionState({
			sipSession: session,
			sessionState: sessionStates.ESTABLISHING
		}));
	});

	// Session SDP handler
	session.on('sdp', (e) => {
		logSipEvent('SDP', e);
		logger.debug('SDP:', e.sdp);
	});

	// Session ICE candidate handler
	session.on('icecandidate', (e) => logSipEvent('ICE candidate', e));

	// Session send handler
	session.on('send', (e) => logSipEvent('send', e));

	// Session refer handler
	session.on('refer', (data) => {
		try {
			console.log('RECEIVED REFER:', data);
			logSipEvent('refer', data);
			logger.debug('Received REFER request', data);
		} catch (error) {
			logger.error('Referral handling failed:', error);
			store.dispatch(requestActions.notify({
				type: 'error',
				text: `Failed to handle referral: ${error.message}`
			}));
		}
	});
};

